import { useCallback, useEffect, useState } from 'react';
import { Button, Modal } from '@vwfs-bronson/bronson-react';
import { useNavigate } from 'react-router-dom';
import { AuthTokens } from '../../auth';
import {
  Agent,
  FesConfig,
  Job,
  Schedule,
  Team,
  createSchedule,
  deleteSchedule,
  getAgents,
  getScheduleRuns,
  getSchedules,
  getTeams,
  runScheduleNow,
  updateSchedule,
} from '../../fes';
import { ScheduleFormPanel, ScheduleFormValues, toCreateRequest } from './ScheduleFormPanel';
import { SchedulesTable } from './SchedulesTable';
import { CompactId } from '../CompactId';

const dateFormatter = new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium', timeStyle: 'short' });

function formatDateTime(value: string | undefined): string {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : dateFormatter.format(date);
}

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

export function SchedulesView({ fesConfig, tokens }: { fesConfig: FesConfig; tokens: AuthTokens }) {
  const navigate = useNavigate();
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string>();

  const [editing, setEditing] = useState<Schedule | null | undefined>(undefined);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string>();

  const [busyScheduleId, setBusyScheduleId] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [runsFor, setRunsFor] = useState<Schedule>();
  const [runs, setRuns] = useState<Job[]>([]);
  const [runsError, setRunsError] = useState<string>();

  const loadSchedules = useCallback(async () => {
    const response = await getSchedules(fesConfig, tokens);
    setSchedules(response.data || []);
  }, [fesConfig, tokens]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setLoadError(undefined);

      try {
        const [scheduleResponse, agentResponse, teamResponse] = await Promise.all([
          getSchedules(fesConfig, tokens),
          getAgents(fesConfig, tokens),
          getTeams(fesConfig, tokens),
        ]);

        if (cancelled) {
          return;
        }

        setSchedules(scheduleResponse.data || []);
        setAgents(agentResponse.data || []);
        setTeams(teamResponse.data || []);
      } catch (error) {
        if (!cancelled) {
          setLoadError(errorMessage(error, 'Unable to load schedules.'));
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [fesConfig, tokens]);

  const agentsById = new Map(agents.map(agent => [agent.agentId, agent]));

  const handleSave = async (values: ScheduleFormValues) => {
    setIsSaving(true);
    setSaveError(undefined);

    try {
      const request = toCreateRequest(values);

      if (editing) {
        await updateSchedule(fesConfig, tokens, editing.scheduleId, request);
      } else {
        await createSchedule(fesConfig, tokens, values.teamId, request);
      }

      await loadSchedules();
      setEditing(undefined);
      setNotice(editing ? 'Schedule updated.' : 'Schedule created.');
    } catch (error) {
      setSaveError(errorMessage(error, 'Unable to save the schedule.'));
    } finally {
      setIsSaving(false);
    }
  };

  const withBusy = async (schedule: Schedule, action: () => Promise<string>) => {
    setBusyScheduleId(schedule.scheduleId);
    setNotice(undefined);
    setLoadError(undefined);

    try {
      const message = await action();
      await loadSchedules();
      setNotice(message);
    } catch (error) {
      setLoadError(errorMessage(error, 'The action failed.'));
    } finally {
      setBusyScheduleId(undefined);
    }
  };

  const handleToggleEnabled = (schedule: Schedule) =>
    withBusy(schedule, async () => {
      await updateSchedule(fesConfig, tokens, schedule.scheduleId, { enabled: !schedule.enabled });
      return schedule.enabled ? `${schedule.name} paused.` : `${schedule.name} resumed.`;
    });

  const handleRunNow = (schedule: Schedule) =>
    withBusy(schedule, async () => {
      const result = await runScheduleNow(fesConfig, tokens, schedule.scheduleId);

      if (result.status === 'DISPATCHED') {
        return `${schedule.name} queued a job (${result.requestId}).`;
      }
      return `${schedule.name}: ${result.message ?? result.status ?? 'no job was started'}.`;
    });

  const handleDelete = (schedule: Schedule) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete "${schedule.name}"? Jobs it already created are kept.`)) {
      return;
    }

    void withBusy(schedule, async () => {
      await deleteSchedule(fesConfig, tokens, schedule.scheduleId);
      return `${schedule.name} deleted.`;
    });
  };

  const handleShowRuns = async (schedule: Schedule) => {
    setRunsFor(schedule);
    setRuns([]);
    setRunsError(undefined);

    try {
      const response = await getScheduleRuns(fesConfig, tokens, schedule.scheduleId);
      setRuns(response.data || []);
    } catch (error) {
      setRunsError(errorMessage(error, 'Unable to load the run history.'));
    }
  };

  if (isLoading) {
    return <p className="app-empty">Loading schedules...</p>;
  }

  return (
    <>
      <div className="app-table-toolbar">
        <Button onClick={() => setEditing(null)}>New schedule</Button>
      </div>

      {loadError ? (
        <div className="app-result app-team-error" role="alert">
          {loadError}
        </div>
      ) : null}
      {notice ? (
        <div className="app-result" role="status">
          {notice}
        </div>
      ) : null}

      <SchedulesTable
        schedules={schedules}
        agentsById={agentsById}
        busyScheduleId={busyScheduleId}
        onEdit={setEditing}
        onToggleEnabled={handleToggleEnabled}
        onRunNow={handleRunNow}
        onDelete={handleDelete}
        onShowRuns={handleShowRuns}
      />

      {editing !== undefined ? (
        <ScheduleFormPanel
          schedule={editing}
          agents={agents}
          teams={teams}
          saveError={saveError}
          isSaving={isSaving}
          onSave={handleSave}
          onClose={() => {
            setEditing(undefined);
            setSaveError(undefined);
          }}
        />
      ) : null}

      {runsFor ? (
        <Modal
          buttonCloseLabel="Close run history"
          buttonConfirmText="Close"
          onClose={() => setRunsFor(undefined)}
          onConfirm={() => setRunsFor(undefined)}
          shown
          title={`Runs of ${runsFor.name}`}
        >
          {runsError ? (
            <div className="app-result app-team-error" role="alert">
              {runsError}
            </div>
          ) : null}
          {runs.length === 0 && !runsError ? (
            <p className="app-empty">This schedule has not produced any jobs yet.</p>
          ) : (
            <ul className="app-schedule-runs">
              {runs.map(job => (
                <li key={job.requestId}>
                  <span><CompactId label="request ID" value={job.requestId} /> — {job.status} — {formatDateTime(job.createdAt)}</span>
                  <Button link simple small onClick={() => navigate(`/jobs?search=${encodeURIComponent(job.requestId)}`)}>
                    Open job
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </Modal>
      ) : null}
    </>
  );
}
